import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import type { ApprovalMode } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import { staticAggregators } from "@/domains/swap-aggregators/static-aggregators"
import { SwapAggregatorRepository } from "@/domains/swap-aggregators/swap-aggregator-repository"

import type { IApprovalModeProvider } from "./interfaces"

export type NotificationPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right"

const APPROVAL_MODE_KEY = "approval-mode"
const NOTIFICATION_POSITION_KEY = "notification-position"
const THEME_MODE_ENABLED_KEY = "theme-mode-enabled"

// Slippage param sources following the multi-source pattern
export interface SlippageParamSources {
  urlParam: number | undefined
  localstorage: number | undefined
  default: number
  ui: number | undefined
}

export class SettingsStore implements IApprovalModeProvider {
  public approvalMode: ApprovalMode = "infinite"
  public notificationPosition: NotificationPosition = "top-right"
  public themeModeEnabled: boolean = false
  public SwapAggregators: SwapAggregatorRepository

  slippageParamSources: SlippageParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 0.5,
    ui: undefined,
  }

  constructor(
    private storage: IStorageBackend,
    private urlParams: IUrlParams,
    private eventBus?: IEventBus,
  ) {
    // Initialize swap aggregators repository
    this.SwapAggregators = new SwapAggregatorRepository()
    this.SwapAggregators.loadStaticAggregators(staticAggregators, this.eventBus)

    makeObservable(this, {
      approvalMode: observable,
      notificationPosition: observable,
      themeModeEnabled: observable,
      slippageParamSources: observable,
      slippage: computed,
      setApprovalMode: action,
      setNotificationPosition: action,
      setThemeModeEnabled: action,
      setSlippageFromUrlParam: action,
      setSlippageFromLocalStorage: action,
      setSlippageFromUi: action,
    })

    // Load settings from localStorage
    this.loadSettings()
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
      this.initializeFromUrlParams()
    })
  }

  get slippage(): number {
    return (
      this.slippageParamSources.ui ??
      this.slippageParamSources.urlParam ??
      this.slippageParamSources.localstorage ??
      this.slippageParamSources.default
    )
  }

  getApprovalMode(): ApprovalMode {
    return this.approvalMode
  }

  setApprovalMode(mode: ApprovalMode): void {
    this.approvalMode = mode
    this.saveSettings()
  }

  setNotificationPosition(position: NotificationPosition): void {
    this.notificationPosition = position
    this.saveSettings()
  }

  setThemeModeEnabled(enabled: boolean): void {
    this.themeModeEnabled = enabled
    this.saveSettings()
  }

  setSlippageFromUrlParam(value: number | undefined): void {
    if (value === undefined) return
    if (this.slippageParamSources.urlParam === value) return
    this.slippageParamSources.urlParam = value
  }

  setSlippageFromLocalStorage(value: number | undefined): void {
    if (value === undefined) return
    if (this.slippageParamSources.localstorage === value) return
    this.slippageParamSources.localstorage = value
  }

  setSlippageFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.slippageParamSources.ui === value) return
    this.slippageParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem("settings-slippage", value.toString())
    this.urlParams.setParam("slippage", value.toString())
  }

  private initializeFromStorage(): void {
    // Load approval mode
    const savedApprovalMode = this.storage.getItem(APPROVAL_MODE_KEY)
    if (savedApprovalMode && (savedApprovalMode === "infinite" || savedApprovalMode === "strict")) {
      this.approvalMode = savedApprovalMode as ApprovalMode
    }

    // Load notification position
    const savedNotificationPosition = this.storage.getItem(NOTIFICATION_POSITION_KEY)
    if (
      savedNotificationPosition &&
      ["top-left", "top-right", "bottom-left", "bottom-right"].includes(savedNotificationPosition)
    ) {
      this.notificationPosition = savedNotificationPosition as NotificationPosition
    }

    // Load theme mode enabled
    const savedThemeModeEnabled = this.storage.getItem(THEME_MODE_ENABLED_KEY)
    if (savedThemeModeEnabled !== null) {
      this.themeModeEnabled = savedThemeModeEnabled === "true"
    }

    // Initialize slippage from localStorage
    const localSlippage = this.storage.getItem("settings-slippage")
    if (localSlippage) {
      const parsedSlippage = Number(localSlippage)
      if (!isNaN(parsedSlippage)) {
        this.setSlippageFromLocalStorage(parsedSlippage)
      }
    }
  }

  private initializeFromUrlParams(): void {
    // Initialize slippage from URL params
    const urlSlippage = this.urlParams.getParam("slippage")
    if (urlSlippage) {
      const parsedSlippage = Number(urlSlippage)
      if (!isNaN(parsedSlippage)) {
        this.setSlippageFromUrlParam(parsedSlippage)
      }
    }

    // Initialize theme mode from URL params
    const urlManageTheme = this.urlParams.getParam("manageTheme")
    if (urlManageTheme === "true") {
      this.setThemeModeEnabled(true)
    }
  }

  private loadSettings(): void {
    // Legacy method - now using initializeFromStorage
    this.initializeFromStorage()
  }

  private saveSettings(): void {
    this.storage.setItem(APPROVAL_MODE_KEY, this.approvalMode)
    this.storage.setItem(NOTIFICATION_POSITION_KEY, this.notificationPosition)
    this.storage.setItem(THEME_MODE_ENABLED_KEY, this.themeModeEnabled.toString())
  }
}
